import { useDroppable } from '@dnd-kit/core';
import { memo, useMemo } from 'react';

import { cn } from '@/ui/lib/utils';

import type { CalendarDroppableData } from '../../hooks/useCalendarDrag';
import type { CalendarEvent } from '../../types';
import {
  endOfDayInTimeZone,
  isSameDayInTimeZone,
  startOfDayInTimeZone
} from '../../utils/dateGrid';
import { greedyAssignLanes } from '../../utils/eventLayout';
import EventBlock from '../EventBlock';

import type { AllDayRowProps } from './types';

interface SpanningEvent {
  event: CalendarEvent;
  startCol: number;
  endCol: number;
  lane: number;
}

function assignLanes(spans: Omit<SpanningEvent, 'lane'>[]): SpanningEvent[] {
  const sorted = [...spans].sort((a, b) => a.startCol - b.startCol);
  const lanes = greedyAssignLanes(
    sorted,
    (span) => span.startCol,
    (span) => span.endCol,
    // Inclusive integer columns: a span starting on the column the previous
    // one ends on still overlaps it in that shared day, unlike continuous time.
    (laneEndCol, startCol) => laneEndCol < startCol
  );
  return sorted.map((span, i) => ({ ...span, lane: lanes[i] }));
}

interface AllDayCellProps {
  day: Date;
  index: number;
  timeZone: string;
  onSlotClick?: AllDayRowProps['onSlotClick'];
}

/** A single droppable day cell in the all-day row, so dragging an all-day/multi-day
 * event resolves a target day from `dragEvent.over` instead of falling back to a
 * vertical-pixel-delta guess (which is always ~0 for horizontal-only drags here). */
function AllDayCell({ day, index, timeZone, onSlotClick }: AllDayCellProps) {
  const dayStart = startOfDayInTimeZone(day, timeZone);
  const dayEnd = endOfDayInTimeZone(day, timeZone);
  const droppableData: CalendarDroppableData = { day: dayStart, wholeDay: true };
  const { setNodeRef } = useDroppable({
    id: `all-day-cell-${dayStart.toISOString()}`,
    data: droppableData
  });

  return (
    <div
      ref={setNodeRef}
      className="cursor-pointer border-l border-uniform-gray-100 first:border-l-0 dark:border-uniform-gray-800"
      style={{ gridColumn: index + 1, gridRow: 1 }}
      onClick={() => onSlotClick?.({ start: dayStart, end: dayEnd, isAllDay: true })}
    />
  );
}

function AllDayRow({
  days,
  events,
  timeZone,
  className,
  onEventClick,
  onSlotClick,
  renderEventContent,
  renderEventPopover
}: AllDayRowProps) {
  const rangeStart = startOfDayInTimeZone(days[0], timeZone);
  const rangeEnd = endOfDayInTimeZone(days[days.length - 1], timeZone);

  const spans = useMemo(() => {
    const candidates = events.filter(
      (event) =>
        (event.isAllDay || !isSameDayInTimeZone(event.start, event.end, timeZone)) &&
        event.start < rangeEnd &&
        event.end > rangeStart
    );

    const raw = candidates.map((event) => {
      const startCol = Math.max(
        0,
        days.findIndex((day) => event.start < endOfDayInTimeZone(day, timeZone))
      );
      let endCol = days.length - 1;
      for (let i = days.length - 1; i >= 0; i--) {
        if (event.end > startOfDayInTimeZone(days[i], timeZone)) {
          endCol = i;
          break;
        }
      }
      return { event, startCol, endCol: Math.max(endCol, startCol) };
    });

    return assignLanes(raw);
  }, [events, days, rangeStart, rangeEnd, timeZone]);

  const laneCount = spans.reduce((max, span) => Math.max(max, span.lane + 1), 1);

  return (
    <div
      className={cn(
        'grid border-b border-uniform-gray-100 dark:border-uniform-gray-800',
        className
      )}
      style={{
        gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
        minHeight: laneCount * 24
      }}>
      {days.map((day, index) => (
        <AllDayCell
          key={day.toISOString()}
          day={day}
          index={index}
          timeZone={timeZone}
          onSlotClick={onSlotClick}
        />
      ))}
      {spans.map(({ event, startCol, endCol, lane }) => (
        <div
          key={event.id}
          className="relative px-0.5 py-0.5"
          style={{
            gridColumn: `${startCol + 1} / ${endCol + 2}`,
            gridRow: 1,
            marginTop: lane * 22
          }}>
          <EventBlock
            event={event}
            timeZone={timeZone}
            compact
            onClick={onEventClick}
            renderEventContent={renderEventContent}
            renderEventPopover={renderEventPopover}
          />
        </div>
      ))}
    </div>
  );
}

export default memo(AllDayRow);
